/**
 * Korean labels for every backend enum we render. One table so a status never
 * shows up as a raw SCREAMING_CASE token in the UI.
 */

import type {
  AccountType,
  BacktestLabel,
  BookEntryKind,
  BookVerdict,
  Cadence,
  ConditionalOrderStatus,
  ConditionalOrderType,
  IndicatorSource,
  Market,
  OrderSide,
  OrderStatus,
  OrderType,
  PredicateOp,
  ProtectionState,
  RankingDuration,
  RankingType,
  RunState,
  SecurityType,
  SizingMode,
  StrategyStatus,
  TimeInForce,
  WarningType,
} from "@/types/api";

/**
 * 시계열 조회 기간. 서버는 `days` 일 수만 받아요(성과 1~365, 추이 2~365) — 라벨은 사람
 * 말이고 값은 일 수입니다. 대시보드와 성과 화면이 **같은 표**를 써요. 갈라지면 두 화면이
 * "3개월" 로 서로 다른 구간을 그립니다.
 */
export const HISTORY_RANGES = [
  { value: "30", label: "1개월" },
  { value: "90", label: "3개월" },
  { value: "180", label: "6개월" },
  { value: "365", label: "1년" },
];

export const STRATEGY_STATUS: Record<StrategyStatus, string> = {
  DRAFT: "작성 중",
  ACTIVE: "실행 중",
  PAUSED: "일시정지",
  HALTED: "자동정지",
  ARCHIVED: "보관",
};

export const STRATEGY_STATUS_TONE: Record<
  StrategyStatus,
  "neutral" | "primary" | "positive" | "negative" | "cautionary"
> = {
  DRAFT: "neutral",
  /*
    실행 중은 초록이 아니라 브랜드 블루예요(`DESIGN.md` — 파랑은 "지금 집행 중인 조각").
    `positive`/`negative` 는 손익 부호 전용이라, 실행 중 배지를 초록으로 칠하면 규칙
    목록이 "이 규칙은 벌고 있다" 로 읽혀요. 상태는 수익이 아닙니다.
  */
  ACTIVE: "primary",
  PAUSED: "cautionary",
  HALTED: "negative",
  ARCHIVED: "neutral",
};

/** 토스증권 계좌 종류. 현재 API 는 BROKERAGE 만 노출한다. */
export const ACCOUNT_TYPE: Record<AccountType, string> = {
  BROKERAGE: "종합매매",
  OVERSEAS_DERIVATIVES: "해외파생",
  PENSION_SAVINGS: "연금저축",
  RESHORING_INVESTMENT: "국내투자",
  unknown: "확인되지 않음",
};

/** 토스 `Order.status` 10종 + 서버 unknown. `null`(응답 전) 은 호출부가 "접수 중" 으로 그린다. */
export const ORDER_STATUS: Record<OrderStatus, string> = {
  PENDING: "미체결",
  PENDING_CANCEL: "취소 접수",
  PENDING_REPLACE: "정정 접수",
  PARTIAL_FILLED: "일부체결",
  FILLED: "체결",
  CANCELED: "취소",
  REJECTED: "거부",
  CANCEL_REJECTED: "취소 거부",
  REPLACE_REJECTED: "정정 거부",
  REPLACED: "정정됨",
  unknown: "확인 중",
};

export const ORDER_STATUS_TONE: Record<
  OrderStatus,
  "neutral" | "primary" | "positive" | "negative" | "cautionary"
> = {
  PENDING: "primary",
  PENDING_CANCEL: "cautionary",
  PENDING_REPLACE: "cautionary",
  PARTIAL_FILLED: "cautionary",
  FILLED: "positive",
  CANCELED: "neutral",
  REJECTED: "negative",
  CANCEL_REJECTED: "negative",
  REPLACE_REJECTED: "negative",
  REPLACED: "neutral",
  unknown: "cautionary",
};

export const ORDER_SIDE: Record<OrderSide, string> = {
  BUY: "매수",
  SELL: "매도",
};
export const ORDER_TYPE: Record<OrderType, string> = {
  LIMIT: "지정가",
  MARKET: "시장가",
};

export const PROTECTION_STATE: Record<ProtectionState, string> = {
  NONE: "보호 없음",
  PENDING: "등록 중",
  HARD: "토스 조건주문",
  SOFT: "서버 의존 보호",
  FAILED: "등록 실패",
};

/** The sentence that has to sit next to the badge, per PRD §5 ③. */
export const PROTECTION_EXPLANATION: Record<ProtectionState, string> = {
  NONE: "손절·익절이 등록되지 않았어요. 지금은 자동으로 지켜지지 않아요.",
  PENDING: "손절·익절을 토스증권 조건주문에 등록하고 있어요.",
  HARD: "손절·익절이 토스증권 조건주문에 등록돼 있어요. 트랑슈 서버가 멈춰도 그대로 유지돼요.",
  SOFT: "토스증권 조건주문 슬롯을 쓸 수 없어서 트랑슈 서버가 손절선을 지켜보고 있어요. 서버가 동작할 때만 실행돼요.",
  FAILED:
    "조건주문 등록이 실패했어요. 트랑슈 서버가 대신 지켜보고 있지만, 서버가 멈추면 실행되지 않아요.",
};

export const PROTECTION_TONE: Record<
  ProtectionState,
  "positive" | "cautionary" | "negative" | "neutral"
> = {
  NONE: "negative",
  PENDING: "neutral",
  HARD: "positive",
  SOFT: "cautionary",
  FAILED: "negative",
};

export const RUN_STATE: Record<RunState, string> = {
  EVALUATING: "평가 중",
  IDLE: "조건 미충족",
  DATA_STALE: "데이터 지연",
  RUN_FAILED: "평가 실패",
  SIGNAL: "신호 발생",
};

/** 토스 `/rankings` `type` 6종 그대로 — 시가총액·변동성 랭킹은 API 에 없다. */
export const RANKING_TYPE: Record<RankingType, string> = {
  MARKET_TRADING_AMOUNT: "거래대금 상위",
  MARKET_TRADING_VOLUME: "거래량 상위",
  TOP_GAINERS: "상승률 상위",
  TOP_LOSERS: "하락률 상위",
  TOSS_SECURITIES_TRADING_AMOUNT: "토스증권 거래대금 상위",
  TOSS_SECURITIES_TRADING_VOLUME: "토스증권 거래량 상위",
};

/** 토스 `/rankings` `duration` 7종 그대로 — 분 단위 랭킹은 없다. */
export const RANKING_DURATION: Record<RankingDuration, string> = {
  realtime: "실시간",
  "1d": "1일",
  "1w": "1주",
  "1mo": "1개월",
  "3mo": "3개월",
  "6mo": "6개월",
  "1y": "1년",
};

export const CADENCE: Record<Cadence, string> = {
  "1m": "1분마다",
  "3m": "3분마다",
  "5m": "5분마다",
  "15m": "15분마다",
  "30m": "30분마다",
  "60m": "1시간마다",
  "1d": "하루에 한 번",
};

export const SIZING_MODE: Record<SizingMode, string> = {
  fixed_amount: "1회 고정금액",
  pct_of_buying_power: "매수가능금액 비율",
  risk_based: "손실 허용액 기준",
  equal_weight: "균등 분할",
  monthly_budget: "월 적립",
};

/** 서버 `Market` enum → 한국어. 원문(`KR_ETC`)이 화면에 나가지 않게 해요. */
export const MARKET_LABEL: Record<Market, string> = {
  KOSPI: "코스피",
  KOSDAQ: "코스닥",
  NYSE: "뉴욕증권거래소",
  NASDAQ: "나스닥",
  AMEX: "아멕스",
  KR_ETC: "국내 기타",
  US_ETC: "미국 기타",
  unknown: "확인되지 않음",
};

export const SECURITY_TYPE: Record<SecurityType, string> = {
  STOCK: "주식",
  FOREIGN_STOCK: "해외주식",
  ETF: "ETF",
  FOREIGN_ETF: "해외 ETF",
  ETN: "ETN",
  REIT: "리츠",
  DEPOSITARY_RECEIPT: "예탁증서(DR)",
  INFRASTRUCTURE_FUND: "인프라펀드",
  STOCK_WARRANTS: "신주인수권",
  unknown: "기타",
};

export const WARNING_TYPE: Record<WarningType, string> = {
  LIQUIDATION_TRADING: "정리매매",
  OVERHEATED: "투자주의(과열)",
  INVESTMENT_WARNING: "투자경고",
  INVESTMENT_RISK: "투자위험",
  VI_STATIC: "정적 VI 발동",
  VI_DYNAMIC: "동적 VI 발동",
  VI_STATIC_AND_DYNAMIC: "정적·동적 VI 발동",
  STOCK_WARRANTS: "신주인수권",
  unknown: "확인되지 않은 유의사항",
};

/** 토스 조건주문 타입. */
export const CONDITIONAL_TYPE: Record<ConditionalOrderType, string> = {
  SINGLE: "단일 조건",
  OCO: "OCO (동시 감시)",
  OTO: "OTO (순차 감시)",
};

/** 토스 조건주문 상태(그룹+leg) + 서버 전용 상태. */
export const CONDITIONAL_STATUS: Record<ConditionalOrderStatus, string> = {
  WATCHING: "감시 중",
  PAUSED: "일시중지",
  ORDERING: "주문 생성 중",
  ORDERED: "주문 생성됨",
  COMPLETED: "완료",
  EXPIRED: "만료",
  HOLDING: "선행 조건 대기",
  CANCELED: "취소",
  LOCAL_ONLY: "토스 미등록",
  unknown: "확인 중",
};

/** 토스 Time In Force. */
export const TIME_IN_FORCE: Record<TimeInForce, string> = {
  DAY: "당일 유효",
  CLS: "장 마감(LOC)",
  OPG: "장 시작(OPG)",
  unknown: "확인 중",
};

export const PREDICATE_OP: Record<PredicateOp, string> = {
  eq: "같을 때",
  neq: "다를 때",
  gt: "보다 클 때",
  gte: "이상일 때",
  lt: "보다 작을 때",
  lte: "이하일 때",
  between: "사이일 때",
  not_between: "사이를 벗어날 때",
  cross_above: "위로 돌파할 때",
  cross_below: "아래로 이탈할 때",
  rising: "연속으로 오를 때",
  falling: "연속으로 내릴 때",
  in: "중 하나일 때",
  not_in: "에 없을 때",
};

export const INDICATOR_SOURCE: Record<IndicatorSource, string> = {
  candle: "가격·거래량",
  index: "시장 지수",
  bond: "국채 금리",
  orderbook: "호가",
  trades: "체결",
  price: "현재가",
  price_limit: "상·하한가",
  ranking: "랭킹",
  warning: "유의종목",
  stock: "종목 정보",
  holding: "보유 종목",
  position: "포지션",
  fx: "환율",
  investor: "시장 수급",
  session: "장 세션",
};

/**
 * `{n}` is replaced by the indicator's `period` param.
 * Only the labels the natural-language summary needs are here; the rule builder
 * takes its own labels from `GET /strategies/dsl/schema`.
 */
export const INDICATOR_LABEL: Record<string, string> = {
  "candle.sma": "{n}일 이동평균",
  "candle.ema": "{n}일 지수이동평균",
  "candle.wma": "{n}일 가중이동평균",
  "candle.close": "종가",
  "candle.open": "시가",
  "candle.high": "고가",
  "candle.low": "저가",
  "candle.volume": "거래량",
  "candle.rsi": "RSI({n})",
  "candle.macd": "MACD",
  "candle.bbands": "볼린저밴드({n})",
  "candle.atr": "ATR({n})",
  "candle.natr": "정규화 ATR({n})",
  "candle.stoch": "스토캐스틱({n})",
  "candle.adx": "ADX({n})",
  // 아래 10개는 라벨이 없어서 편집기 드롭다운에 `keltner_lower` 처럼 **snake_case 원문이
  // 그대로** 보였어요(QA 에서 관측). 이 표가 존재하는 이유가 바로 그걸 막는 것입니다.
  "candle.keltner_upper": "켈트너 상단(N)",
  "candle.keltner_lower": "켈트너 하단(N)",
  "candle.plus_di": "+DI(N)",
  "candle.minus_di": "-DI(N)",
  "candle.linreg_r2": "회귀 결정계수(N)",
  "candle.upper_wick_pct": "위꼬리 비율",
  "candle.lower_wick_pct": "아래꼬리 비율",
  "candle.opening_range_high": "장 초반 N분 고가",
  "candle.opening_range_low": "장 초반 N분 저가",
  "candle.pattern": "촛대 패턴",
  "candle.obv": "OBV",
  "candle.mfi": "MFI({n})",
  "candle.cci": "CCI({n})",
  "candle.roc": "{n}일 변화율",
  "candle.momentum": "{n}일 모멘텀",
  "candle.donchian_high": "{n}일 최고가",
  "candle.donchian_low": "{n}일 최저가",
  "candle.donchian_pos": "{n}일 범위 내 위치",
  "candle.stdev": "{n}일 표준편차",
  "candle.hist_vol": "{n}일 역사적 변동성",
  "candle.vwap_session": "당일 VWAP(근사)",
  "candle.volume_ratio": "{n}일 평균 거래량 대비 배수",
  "candle.volume_zscore": "{n}일 거래량 Z점수",
  "candle.gap_pct": "시가 갭",
  "candle.range_pct": "일중 변동폭",
  "candle.body_pct": "몸통 비율",
  "candle.consecutive_up": "연속 상승 봉 수",
  "candle.consecutive_down": "연속 하락 봉 수",
  "candle.high_n": "{n}봉 신고가",
  "candle.low_n": "{n}봉 신저가",
  "candle.pos_in_range_n": "{n}봉 범위 내 위치",
  "candle.return_pct": "{n}봉 수익률",
  "candle.slope": "{n}봉 기울기",
  "price.last": "현재가",
  "price.timestamp_age_s": "시세 경과 시간(초)",
  "orderbook.best_bid": "최우선 매수호가",
  "orderbook.best_ask": "최우선 매도호가",
  "orderbook.mid": "호가 중간값",
  "orderbook.spread": "호가 스프레드",
  "orderbook.spread_bps": "호가 스프레드(bp)",
  "orderbook.imbalance": "매수·매도 잔량비",
  "orderbook.bid_volume_total": "매수 총잔량",
  "orderbook.ask_volume_total": "매도 총잔량",
  "trades.trade_count_per_min": "분당 체결 건수",
  "trades.uptick_ratio": "상승 체결 비율(근사)",
  "price_limit.dist_to_upper_pct": "상한가까지 거리",
  "price_limit.dist_to_lower_pct": "하한가까지 거리",
  "price_limit.is_limit_up": "상한가 여부",
  "price_limit.is_limit_down": "하한가 여부",
  "ranking.rank_in": "랭킹 순위",
  "ranking.change_rate": "전일 대비 등락률",
  "ranking.trading_amount": "거래대금",
  "stock.market_cap": "시가총액",
  "stock.market": "시장",
  "stock.security_type": "종목 유형",
  "stock.leverage_factor": "레버리지 배수",
  "stock.days_since_list": "상장 경과일",
  "investor.foreigner_net": "시장 전체 외국인 순매수",
  "investor.institution_net": "시장 전체 기관 순매수",
  "investor.individual_net": "시장 전체 개인 순매수",
  "holding.pnl_rate": "보유 수익률",
  "holding.weight_in_portfolio": "포트폴리오 내 비중",
  "position.holding_days": "보유 일수",
  "position.holding_bars": "보유 봉 수",
  "position.unrealized_pnl_pct": "평가 수익률",
  "position.entry_price": "평균 매입가",
  "position.quantity": "보유 수량",
  "position.entry_count": "분할 매수 횟수",
  "position.days_since_last_entry": "직전 매수 후 경과일",
  "position.minutes_since_last_entry": "직전 매수 후 경과 분",
  "position.mae_pct": "최대 평가손실률",
  "position.mfe_pct": "최대 평가이익률",
  "position.trailing_high": "보유 중 최고가",
  "session.minutes_since_open": "장 시작 후 경과 분",
  "session.minutes_to_close": "장 마감까지 남은 분",
  // 서버 레지스트리(`indicators/registry.py`)의 세션 지표 넷 중 둘이 빠져 있어서, 프리셋
  // 요약이 "is_business_day 가 1 와 같을 때" 처럼 원시 이름을 그대로 읽어 줬어요.
  "session.is_business_day": "영업일 여부(영업일 = 1)",
  "session.session": "장 구분",
  "fx.usdkrw": "원·달러 환율",
};

/** Index symbols the summary may name. */
export const SYMBOL_LABEL: Record<string, string> = {
  KOSPI: "코스피",
  KOSDAQ: "코스닥",
};

/**
 * 실행 스킵·거부 사유 코드 → 한국어 설명. `Strategy.run.skipped[symbol]` 과
 * `Signal.reject_code` 가 이 값들을 그대로 준다 — 키가 없으면(WARNING_*, VI_HOLD_*,
 * BOOK_*, STATUS_* 처럼 동적으로 조합되는 코드) 호출부가 원본 문자열로 폴백한다.
 * `OPPOSITE_PENDING_FOREIGN`·`SYMBOL_TAKEN_FOREIGN` 은 여기 없으면 화면에 raw
 * SCREAMING_CASE 로 그대로 노출된다.
 */
export const SIGNAL_SKIP_REASON: Record<string, string> = {
  SYMBOL_ORPHANED: "격리된 종목 (계좌 확인 필요)",
  SYMBOL_TAKEN: "다른 규칙이 이미 보유 중",
  SYMBOL_TAKEN_FOREIGN: "토스증권 앱에서 이미 보유 중",
  OPPOSITE_PENDING_ORDER: "우리 반대방향 주문 대기 중",
  OPPOSITE_PENDING_FOREIGN: "토스증권 앱의 반대방향 주문 대기 중",
  ALREADY_IN_FLIGHT: "같은 방향 주문이 이미 진행 중",
  DATA_STALE: "데이터 지연",
  ENTRY_FALSE: "진입 조건 미충족",
  NO_PRICE: "현재가를 가져올 수 없음",
  NO_SIZE: "매수 수량 계산 불가",
  NO_SELLABLE_QUANTITY: "매도 가능 수량 없음",
  SIZING_FAILED: "매수 금액 계산 실패",
  SAFETY_GATE: "안전장치에 막힘",
  RUN_LIMIT: "실행 한도 도달",
  MAX_CONCURRENT_POSITIONS: "동시 보유 한도 도달",
  MASTER_UNKNOWN: "종목 정보를 확인할 수 없음",
  LEVERAGED: "레버리지 상품 제외",
  PREFERRED_SHARE: "우선주 제외",
  LIQUIDATION_TRADING: "정리매매 종목 제외",
  TRADING_SUSPENDED: "거래정지 종목",
  NXT_UNSUPPORTED: "NXT 미지원 종목",
  EXCLUDED: "제외 목록에 포함",
  MAX_SYMBOLS: "유니버스 정원 초과",
  FILTER: "필터 조건 미충족",
  ACCOUNT_DISABLED: "계좌 자동매매 꺼짐",
  SESSION_NOT_ALLOWED: "허용되지 않은 장 시간",
  OUTSIDE_ENTRY_WINDOW: "진입 허용 시간이 아님",
  NOT_SCHEDULED_DAY: "실행 요일이 아님",
  ENTRY_BLOCKED: "진입 차단됨",
  WARNINGS_UNAVAILABLE: "유의사항 조회 실패 (차단)",
  ACCRUAL_INSUFFICIENT: "적립금이 아직 1주 값에 못 미쳐요",
  MONTHLY_BUDGET_REQUIRES_STATIC_UNIVERSE: "월 적립은 직접 지정 종목만 쓸 수 있어요",
};

/**
 * `GET /accounts/{id}/book` verdict → sentence. `OK` has no sentence — nothing
 * is wrong, so nothing needs to be said. `reconcile()`'s precedence order stays
 * server-side; this table only translates whichever one code the server picked.
 */
export const BOOK_VERDICT_MESSAGE: Record<BookVerdict, string | null> = {
  OK: null,
  // 회수 **대상**을 적어요. 예전 문구는 "회수로 확정해 주세요" 로 끝나서 운영자가
  // 규칙 예산에서 회수했고(stage 실측), 규칙 회수는 계좌 장부 청구액을 한 푼도 줄이지
  // 않아서 경고가 그대로 남았어요 — 시킨 대로 했는데 아무 일도 안 일어나는 안내였어요.
  CASH_INVADED:
    "장부가 계좌보다 많아요. 새 진입을 멈췄어요. 「자금 배정 · 회수」에서 적용 대상을 “계좌 전체” 로 골라, 앱에서 쓴 금액을 회수해 주세요.",
  SYMBOL_INVADED: "규칙 물량이 계좌와 맞지 않는 종목이 있어요. 그 종목만 멈췄어요.",
  STALE: "계좌 정보가 방금 주문보다 오래됐어요. 잠시 뒤 다시 판정해요.",
  BOOK_MISSING: "규칙에 쓸 금액을 아직 배정하지 않았어요.",
};

export const BOOK_VERDICT_TONE: Record<
  BookVerdict,
  "neutral" | "positive" | "negative" | "cautionary"
> = {
  OK: "positive",
  CASH_INVADED: "negative",
  SYMBOL_INVADED: "negative",
  STALE: "cautionary",
  BOOK_MISSING: "neutral",
};

/** `GET /accounts/{id}/book/entries` 한 줄의 `kind`. */
export const BOOK_ENTRY_KIND: Record<BookEntryKind, string> = {
  RESERVE: "예약",
  RELEASE: "예약 해제",
  BUY_FILL: "매수 체결",
  SELL_FILL: "매도 체결",
  FUND: "배정",
  WITHDRAW: "회수",
  SETTLE: "결제",
  ADJUST: "조정",
  QUARANTINE: "격리",
};

export const BOOK_ENTRY_KIND_TONE: Record<
  BookEntryKind,
  "neutral" | "primary" | "positive" | "negative" | "cautionary"
> = {
  RESERVE: "neutral",
  RELEASE: "neutral",
  BUY_FILL: "primary",
  SELL_FILL: "primary",
  FUND: "positive",
  WITHDRAW: "cautionary",
  SETTLE: "neutral",
  ADJUST: "cautionary",
  QUARANTINE: "negative",
};

/** B12 backtest labels — badge text plus the tooltip that must accompany it. */
export const BACKTEST_LABEL: Record<BacktestLabel, { text: string; tooltip: string }> = {
  SIMULATED: {
    text: "SIMULATED",
    tooltip: "과거 데이터로 계산한 모의 결과예요. 실제 체결·수익이 아니에요.",
  },
  INTRABAR_PESSIMISTIC: {
    text: "봉 내부 비관 가정",
    tooltip:
      "한 봉 안에서 손절과 익절이 모두 닿는 경우, 항상 손절이 먼저 체결된 것으로 계산했어요.",
  },
  SLIPPAGE_MODELED: {
    text: "슬리피지 모델",
    tooltip: "과거 호가창을 조회할 수 없어서 국내 10bp·미국 5bp 고정 슬리피지를 적용했어요.",
  },
  UNIVERSE_APPROXIMATED: {
    text: "유니버스 근사",
    tooltip:
      "랭킹 API 는 현재 시점만 제공해요. 스냅샷이 있는 구간은 실제 랭킹, 그 이전 구간은 현재 랭킹을 고정해서 계산했어요.",
  },
  CURRENT_DATA_SUBSTITUTED: {
    text: "현재값 대체",
    tooltip:
      "종목 정보·랭킹·환율은 과거 시점 값을 조회할 수 없어서 현재 값으로 계산했어요. 그 조건을 쓰는 규칙은 결과가 실제와 다를 수 있어요.",
  },
  SURVIVORSHIP_RISK: {
    text: "생존편향 가능",
    tooltip: "과거 유의종목·상장폐지 이력을 조회할 수 없어서 현재 종목 정보 기준으로 계산했어요.",
  },
  TAX_ESTIMATED: {
    text: "세금 추정치",
    tooltip: "사전 세율 조회가 없어서 설정된 세율표로 추정했어요. 실제 세금과 다를 수 있어요.",
  },
  INVESTOR_MARKET_LEVEL_ONLY: {
    text: "수급 시장 단위",
    tooltip: "투자자별 매매 데이터는 코스피·코스닥 시장 전체만 제공돼요. 개별 종목 수급은 없어요.",
  },
  DATA_INCOMPLETE: {
    text: "데이터 결측",
    tooltip: "결측 봉이 5%를 넘어요. 결과를 참고용으로만 봐 주세요.",
  },
  WINDOW_LIMITED: {
    text: "기간 제한",
    tooltip: "1분봉 백테스트는 기본 90일까지만 계산해요.",
  },
};
